import TradeResultPanel from '@/components/trading/TradeResultPanel';
import TradeTypeBadges from '@/components/trading/TradeTypeBadges';
import { getStateLabel, getStateBadgeColor } from '@/utils/trading-recommendation-helpers';
import { useDarkMode } from '@/hooks/useDarkMode';
import type { RecommendationSwingtrade, LongTermRecommendation } from '@/models';

interface ShareTradeCardProps {
  trade: RecommendationSwingtrade | LongTermRecommendation;
}

/**
 * Shareable image version of the mobile history result card.
 *
 * Deliberately free of `lg:` variants and of interactive elements (no favorite control,
 * no expand chevron, no links): the rendered PNG must look the same regardless of the
 * viewport it was captured at, and the component has to render outside of any router or
 * user context. Rendered off-screen by ShareTradeImageButton, never on the page itself.
 */
export default function ShareTradeCard({ trade }: ShareTradeCardProps) {
  const { isDarkMode } = useDarkMode();
  const currency = trade.symbol?.currentData?.currency ?? 'USD';
  const exchange = trade.stockExchange?.identifier;

  // No padding and no background around the card: the PNG is cropped tight to the card
  // itself. Anything outside it would be transparent, and viewers that flatten alpha
  // (macOS Preview, WhatsApp) would render that as a white frame.
  return (
    <div>
      <div className={`bg-white dark:bg-gray-800 rounded-lg overflow-hidden ${
        trade.boost
          ? 'border-2 border-amber-400 dark:border-amber-500'
          : 'border border-gray-200 dark:border-gray-700'
      }`}>
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {/* Symbol */}
            <div className="min-w-0">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Symbol</div>
              {/* Block-level flex, not inline-flex: when exchange and ticker wrap onto two
                  lines the container has to grow, otherwise the overflow collides with the
                  badges below. */}
              <div className="text-lg font-bold text-gray-900 dark:text-white flex flex-wrap items-center min-w-0">
                {exchange !== null && exchange !== undefined ? <span>{exchange}:</span> : null}
                <span>{trade.symbolTicker}</span>
              </div>
              <TradeTypeBadges type={trade.type} boost={trade.boost} free={trade.free} />
            </div>

            {/* Status */}
            <div className="flex flex-col">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Status</div>
              <div className="flex-1 flex items-center">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStateBadgeColor(trade)}`}>
                  {getStateLabel(trade)}
                </span>
              </div>
            </div>
          </div>

          <TradeResultPanel
            averageBuyPrice={trade.averageBuyPrice}
            averageExitPrice={trade.averageExitPrice}
            yieldValue={trade.yield}
            currency={currency}
          />

          {/* Branding */}
          <div className="flex items-center justify-between">
            <img
              src={isDarkMode ? '/logo_light.png' : '/logo.png'}
              alt="BolzAI"
              className="h-5 w-auto"
            />
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500">boersencampus.svenbolz.com</span>
          </div>
        </div>
      </div>
    </div>
  );
}
